import { STORAGE_KEY, getSessionId } from "@/utils/notificationStorage";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface NotificationState {
  // Campaign IDs that user has dismissed (won't show again)
  dismissedCampaignIds: string[];

  // Campaign IDs that user has seen (clicked bell or viewed dropdown)
  seenCampaignIds: string[];

  // Last session where tooltip was shown
  lastTooltipSessionId: string | null;

  // Whether the notification dropdown/sheet is currently open
  isDropdownOpen: boolean;

  // Actions
  dismissCampaign: (campaignId: string) => void;
  markCampaignAsSeen: (campaignId: string) => void;
  markAllAsSeen: (campaignIds: string[]) => void;
  setDropdownOpen: (isOpen: boolean) => void;
  shouldShowTooltip: (campaignIds: string[]) => boolean;
  markTooltipShown: () => void;
}

const initialState = {
  dismissedCampaignIds: [],
  seenCampaignIds: [],
  lastTooltipSessionId: null,
  isDropdownOpen: false,
};

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      ...initialState,

      dismissCampaign: (campaignId: string) => {
        set((state) => ({
          dismissedCampaignIds: state.dismissedCampaignIds.includes(campaignId)
            ? state.dismissedCampaignIds
            : [...state.dismissedCampaignIds, campaignId],
        }));
      },

      markCampaignAsSeen: (campaignId: string) => {
        set((state) => ({
          seenCampaignIds: state.seenCampaignIds.includes(campaignId)
            ? state.seenCampaignIds
            : [...state.seenCampaignIds, campaignId],
        }));
      },

      markAllAsSeen: (campaignIds: string[]) => {
        const state = get();
        const nextSeen = new Set([...state.seenCampaignIds, ...campaignIds]);
        if (nextSeen.size === state.seenCampaignIds.length) {
          return;
        }
        set({ seenCampaignIds: Array.from(nextSeen) });
      },

      setDropdownOpen: (isOpen: boolean) => {
        set({ isDropdownOpen: isOpen });
      },

      shouldShowTooltip: (campaignIds: string[]) => {
        const state = get();
        const currentSessionId = getSessionId();

        // Already shown tooltip this session
        if (state.lastTooltipSessionId === currentSessionId) {
          return false;
        }

        // Check if there are any campaigns user hasn't seen yet
        const unseenCampaigns = campaignIds.filter(
          (id) => !state.seenCampaignIds.includes(id) && !state.dismissedCampaignIds.includes(id),
        );

        return unseenCampaigns.length > 0;
      },

      markTooltipShown: () => {
        set({ lastTooltipSessionId: getSessionId() });
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        // Only persist these fields to localStorage
        dismissedCampaignIds: state.dismissedCampaignIds,
        seenCampaignIds: state.seenCampaignIds,
        // Don't persist: lastTooltipSessionId (session-based), isDropdownOpen (UI state)
      }),
    },
  ),
);

/**
 * Hook to get notification state with computed properties
 */
export const useNotificationState = () => {
  const store = useNotificationStore();

  return {
    ...store,
    // Computed: get campaigns that should be shown (not dismissed)
    getVisibleCampaignIds: (allCampaignIds: string[]) =>
      allCampaignIds.filter((id) => !store.dismissedCampaignIds.includes(id)),

    // Computed: check if there are unseen campaigns
    hasUnseenCampaigns: (campaignIds: string[]) =>
      campaignIds.some((id) => !store.seenCampaignIds.includes(id) && !store.dismissedCampaignIds.includes(id)),
  };
};
